import React, { FC, memo, useState } from 'react';

import { Typography } from '@rbilabs/universal-components';
import { useEnableServiceRewardsBrowse } from 'features/reward/state/service-rewards-context';
import { StyleSheet, View } from 'react-native';

import { ActionButtonVariants, ActionLink } from 'components/action-button';
import PointsIcon from 'components/icons/loyalty-points-icon';
import { PullToReloadScrollView } from 'components/pull-to-reload/pull-to-reload';
import { QuestActivationSource, QuestSection } from 'components/quests';
import { useLoyaltyQuestQuery } from 'components/quests/loyalty-quest/use-loyalty-quest-query';
import { centeredRowContainerStyles } from 'components/ui/centered-row-container';
import { LoyaltyCmsType } from 'generated/graphql-gateway';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { routes } from 'navigation/routing';
import { AccountLink } from 'pages/account/account-link';
import { AccountIcon } from 'pages/account/icons/account-icon';
import { CommunicationPreferencesIcon } from 'pages/account/icons/communication-preferences-icon';
import { PartnershipsIcon } from 'pages/account/icons/partnerships-icon';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { AllowedCelebrationTypes } from 'state/loyalty/hooks/types';
import { useLoyaltyCelebrations } from 'state/loyalty/hooks/use-loyalty-celebrations';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useLoyaltyContext } from 'state/loyalty/loyalty-context';
import { primitive, spacing } from 'styles/constants';
import { WEB_EXTRA_SMALL_MAX_WIDTH, WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { isMobile } from 'utils/environment';
import { waitForDuration } from 'utils/wait-for-duration';

import { useGetLoyaltyBadges } from '../loyalty-badges/use-get-loyalty-badges';
import { LoyaltyNotEnrolled } from '../loyalty-not-enrolled';

import { BadgesSection } from './badges-section/badges-section';
import { ProfileCard } from './profile-card/profile-card';
import { UnauthenticatedProfile } from './unauthenticated-profile/unauthenticated-profile';
import { UserJourneyChallenge } from './user-journey-challenge/user-journey-challenge';

/**
 *
 * LoyaltyProfile displays the user's loyalty profile
 *
 */
export const LoyaltyProfile: FC = memo(() => {
  useEnableServiceRewardsBrowse();
  const [isForceRefreshing, setForceRefresh] = useState<boolean>(false);
  const { user, loading: loadingUser, isAuthenticated } = useAuthContext();
  const { loyaltyUser } = useLoyaltyUser();
  const { refetchLoyaltyUser } = useLoyaltyContext();
  const {
    loading: rewardDataIsLoading,
    engineRewardsMap,
    refetchRewards,
  } = useLoyaltyRewardsList();
  const enabledBadges = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_BADGES);
  const enableWalmartLink = useFlag(LaunchDarklyFlag.ENABLE_WALMART_PLUS_INTEGRATION);
  const enableLoyaltyCelebrations = useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_CELEBRATIONS);
  const enableUserJourneyChallenge = useFlag(LaunchDarklyFlag.ENABLE_USER_JOURNEY_CHALLENGE);
  const { initialLoading: badgesLoading, badges, refetch: refetchBadges } = useGetLoyaltyBadges({
    skip: !enabledBadges,
  });

  const { recheckCelebrations } = useLoyaltyCelebrations({
    allowedCelebrationTypes: [
      AllowedCelebrationTypes.PROFILE_CHALLENGE_COMPLETED,
      AllowedCelebrationTypes.BADGE_EARNED,
      AllowedCelebrationTypes.PARTIAL_PROFILE_CHALLENGE_COMPLETED,
    ],
    disableCelebrations: !enableLoyaltyCelebrations,
    onBadgeCelebration: refetchBadges,
  });

  const loyaltyId = user?.loyaltyId ?? '';

  const {
    loadingQuests: userJourneyChallengeLoading,
    refetchQuests: refetchUserJourneyChallenge,
    quest: userJourneyChallenge,
  } = useLoyaltyQuestQuery({
    loyaltyId,
    cmsType: LoyaltyCmsType.USER_JOURNEY,
    refecthOnFocus: true,
  });

  const forceRefresh = async () => {
    // We need this delay in order to avoid cutting the spinning animation
    await waitForDuration(1500);
    if (loyaltyId) {
      setForceRefresh(true);
      await Promise.allSettled([
        refetchLoyaltyUser(),
        refetchRewards(loyaltyId),
        refetchBadges(),
        refetchUserJourneyChallenge(),
      ]);
      setForceRefresh(false);
      recheckCelebrations();
    }
  };

  if (isAuthenticated && !loyaltyId && !loadingUser) {
    return <LoyaltyNotEnrolled />;
  }

  const isLoading = rewardDataIsLoading || loadingUser || isForceRefreshing;

  return (
    <PullToReloadScrollView
      progressViewOffset={10}
      onRefresh={forceRefresh}
      contentContainerStyle={centeredRowContainerStyles.container}
      isDisabled={!isAuthenticated}
    >
      {!isAuthenticated ? (
        <UnauthenticatedProfile />
      ) : (
        <View style={styles.pageContainer}>
          <View style={styles.profileCardContainer}>
            <View style={[styles.wrapper, styles.xsWrapper]}>
              <ProfileCard
                loyaltyUser={loyaltyUser}
                engineRewardsMap={engineRewardsMap}
                loading={isLoading}
              />
            </View>
          </View>

          {enableUserJourneyChallenge && (
            <UserJourneyChallenge
              loading={userJourneyChallengeLoading && !userJourneyChallenge}
              challenge={userJourneyChallenge}
            />
          )}

          <View style={[styles.wrapper, styles.questSectionContainer]}>
            <QuestSection
              showSmallHeader
              activationSource={QuestActivationSource.REWARDS_PROFILE}
            />
          </View>

          {enabledBadges && (
            <BadgesSection badges={badges} loading={badgesLoading || isForceRefreshing} />
          )}

          <View style={styles.wrapper}>
            <View style={styles.separator} />

            <View style={styles.claimPointsContainer}>
              <Typography>
                {t('claimPointsLinkMessage', {
                  defaultValue: 'Claim missing Crowns with your receipt’s Transaction ID',
                })}
              </Typography>
              <ActionLink
                to={routes.claimPointsBase}
                startIcon={<PointsIcon fill={primitive.$bbqBrown} width={20} height={20} />}
                variant={ActionButtonVariants.OUTLINE}
                style={styles.claimPointsLink}
              >
                {t('enterReceiptCode')}
              </ActionLink>
            </View>

            <AccountLink
              link={routes.accountInfo}
              label={t('accountInfo')}
              leftIcon={<AccountIcon />}
            />
            <AccountLink
              link={routes.communication}
              label={t('communicationPreferences')}
              leftIcon={<CommunicationPreferencesIcon />}
            />

            {enableWalmartLink && (
              <AccountLink
                link={routes.partnerships}
                label={t('partnerships')}
                leftIcon={<PartnershipsIcon />}
              />
            )}
          </View>
        </View>
      )}
    </PullToReloadScrollView>
  );
});

const styles = StyleSheet.create({
  profileCardContainer: {
    width: '100%',
    display: 'flex',
    paddingTop: isMobile() ? spacing.$7 : spacing.$9,
    paddingBottom: isMobile() ? spacing.$6 : spacing.$7,
    backgroundColor: primitive.$lightMustard,
  },
  wrapper: {
    ...centeredRowContainerStyles.content,
    paddingHorizontal: spacing.$4,
    maxWidth: WEB_SMALL_MAX_WIDTH,
  },
  xsWrapper: {
    ...(!isMobile() && {
      maxWidth: WEB_EXTRA_SMALL_MAX_WIDTH,
      boxSizing: 'content-box',
    }),
  },
  pageContainer: {
    width: '100%',
    alignItems: 'center',
    gap: 24,
    paddingBottom: 34,
  },
  claimPointsContainer: {
    width: '100%',
    backgroundColor: primitive.$white,
    borderRadius: primitive.$borderRadius,
    paddingHorizontal: spacing.$3_5,
    paddingVertical: spacing.$6,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.$4,
    shadowColor: primitive.$black,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 1.41,
    elevation: 1,
  },
  claimPointsLink: {
    width: '100%',
    maxWidth: WEB_SMALL_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
  },
  titleText: {
    fontSize: primitive.fontSizes.lg,
    fontFamily: primitive.fontFamily.$headers,
    alignItems: 'flex-end',
    display: 'flex',
  },
  separator: {
    borderTopWidth: 1,
    marginTop: spacing.$4,
    marginBottom: spacing.$6,
  },
  questSectionContainer: {
    width: '100%',
    ...(!isMobile() && {
      maxWidth: WEB_EXTRA_SMALL_MAX_WIDTH,
      boxSizing: 'content-box',
    }),
  },
});
